import { Button } from "@/components/ui/button";
import { Users, Sparkles } from "lucide-react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { TribuCard } from "@/components/tribus/TribuCard";

interface UnirseATribuButtonProps {
  deporte?: string;
  ciudad?: string;
  variant?: "default" | "outline" | "secondary";
  size?: "default" | "sm" | "lg";
  className?: string;
}

export const UnirseATribuButton = ({
  deporte,
  ciudad,
  variant = "outline",
  size = "default",
  className = "",
}: UnirseATribuButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: tribusRecomendadas, isLoading } = useQuery({
    queryKey: ['tribus-recomendadas', deporte, ciudad],
    queryFn: async () => {
      let query = supabase
        .from('tribus')
        .select('*')
        .eq('idioma_verificado', true)
        .order('xp_total', { ascending: false })
        .limit(6);

      // Filtrar por deporte o ciudad si están disponibles
      if (deporte) {
        query = query.ilike('deporte', `%${deporte}%`);
      }
      if (ciudad) {
        query = query.ilike('ciudad', `%${ciudad}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: isOpen,
  });

  const handleOpenDialog = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    setIsOpen(true);
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleOpenDialog}
        className={`gap-2 ${className}`}
      >
        <Users className="w-4 h-4" />
        Unirse a una Tribu
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Sparkles className="w-6 h-6 text-primary" />
              Encuentra tu Tribu Deportiva
            </DialogTitle>
            <DialogDescription className="text-base">
              {deporte || ciudad 
                ? `Tribus recomendadas${deporte ? ` de ${deporte}` : ''}${ciudad ? ` en ${ciudad}` : ''}`
                : 'Descubre las tribus deportivas más activas y únete a la comunidad'}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6">
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-64 bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            ) : tribusRecomendadas && tribusRecomendadas.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tribusRecomendadas.map((tribu) => (
                  <TribuCard key={tribu.id} tribu={tribu} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No hay tribus disponibles aún</h3>
                <p className="text-muted-foreground mb-4">
                  {deporte || ciudad
                    ? 'No encontramos tribus que coincidan. Explora otras tribus o crea una nueva.'
                    : 'Sé el primero en crear una tribu deportiva'}
                </p>
                <Button onClick={() => {
                  setIsOpen(false);
                  navigate('/tribus');
                }}>
                  Explorar Todas las Tribus
                </Button>
              </div>
            )}
          </div>

          <div className="mt-6 text-center border-t pt-4">
            <p className="text-sm text-muted-foreground mb-3">
              ¿No encuentras lo que buscas?
            </p>
            <Button
              variant="default"
              onClick={() => {
                setIsOpen(false);
                navigate('/tribus');
              }}
            >
              Ver Todas las Tribus
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
